<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-medium font-headline antialiased bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Locations
      </h1>
      <FormButton @click="openCreate()" prefix-class="fak" icon="fa-plus" color="blue">Add New State</FormButton>
    </header>

    <div v-if="!isLoading" class="flex flex-col mt-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6 p-4">
        <div class="flex-initial">
          <label for="location-search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput id="location-search" v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="flex-initial">
          <SelectField v-model="query.parent_id" label="State" :options="parentOptions" empty="All States" />
        </div>
        <div v-if="hasFilters" class="flex-initial self-end">
          <FormButton @click="reset" color="blue">Clear Filters</FormButton>
        </div>
      </div>

      <p v-if="!filteredLocations.length" class="text-sm text-bhav-cool-gray-400 italic py-4 px-4">No locations found.</p>

      <div class="px-4 pb-4">
        <div class="bg-white rounded-lg px-4 py-2">
          <template v-for="parent in filteredLocations" :key="parent.id">

            <!-- Parent row -->
            <div class="w-full flex justify-between items-center cursor-pointer py-2 border-b border-bhav-cool-gray-100 last:border-b-0" @click="toggleParent(parent.id)">
              <div class="flex items-center gap-2 mr-2 flex-shrink-0">
                <i class="fas fa-xs text-bhav-blue-200" :class="openParents.has(parent.id) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
              </div>
              <button type="button" class="text-bhav-blue-500 font-medium w-52 shrink-0 text-sm leading-7 text-left truncate hover:text-bhav-blue-700" @click.stop="openEdit(parent)">
                {{ parent.name }}
              </button>
              <p class="text-bhav-blue-200 flex-1 text-sm leading-7">{{ parent.locations?.length ?? 0 }} Regions</p>
              <div class="flex items-center gap-2 ml-2 flex-shrink-0" @click.stop>
                <FormButton @click="openCreate(parent)" prefix-class="fak" icon="fa-plus" color="blue">Add Region</FormButton>
                <FormButton @click="openDelete(parent)" prefix-class="fak" icon="fa-trash" color="red" title="Delete" />
              </div>
            </div>

            <!-- Child locations -->
            <div v-show="openParents.has(parent.id)" class="pl-6">
              <p v-if="!parent.locations?.length" class="py-2 text-sm text-bhav-cool-gray-400 italic">No regions</p>

              <template v-for="child in parent.locations" :key="child.id">

                <!-- Child row -->
                <div class="w-full flex justify-between items-center cursor-pointer py-2 border-b border-bhav-cool-gray-100 last:border-b-0" @click="toggleChild(child.id)">
                  <div class="flex items-center gap-2 mr-2 flex-shrink-0">
                    <i class="fas fa-xs text-bhav-blue-200" :class="openChildren.has(child.id) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                  </div>
                  <button type="button" class="text-bhav-blue-500 w-52 shrink-0 text-sm leading-7 text-left truncate hover:text-bhav-blue-700" @click.stop="openEdit(child)">
                    {{ child.name }}
                  </button>
                  <p class="text-bhav-blue-200 flex-1 text-sm leading-7">{{ child.divisions?.length ?? 0 }} Communities</p>
                  <div class="flex items-center gap-2 ml-2 flex-shrink-0" @click.stop>
                    <FormButton @click="openAttach(child)" prefix-class="fak" icon="fa-plus" color="blue">Add Community</FormButton>
                    <FormButton @click="openDelete(child)" prefix-class="fak" icon="fa-trash" color="red" title="Delete" />
                  </div>
                </div>

                <!-- Communities -->
                <div v-show="openChildren.has(child.id)" class="pl-6">
                  <p v-if="!child.divisions?.length" class="py-2 text-sm text-bhav-cool-gray-400 italic">No communities assigned</p>
                  <div
                    v-for="division in child.divisions"
                    :key="division.id"
                    class="flex items-center justify-between py-2 border-b border-bhav-cool-gray-100 last:border-b-0">
                    <router-link
                      :to="{ name: 'community-show', params: { id: division.id } }"
                      class="text-sm text-bhav-blue-500 hover:text-bhav-blue-700 flex-1 truncate">
                      {{ division.meta?.info?.name || division.slug }}
                    </router-link>
                    <FormButton @click="openDetach(division, child)" prefix-class="fak" icon="fa-detach" color="red" title="Detach" />
                  </div>
                </div>

              </template>
            </div>

          </template>
        </div>
      </div>
    </div>

    <!-- Create / Add Region modal -->
    <ModalDialog v-if="creating" :open="!!creating" @close="cancelCreate" :no-padding="true">
      <template #header>
        <h2 class="text-lg text-bhav-blue-600 font-semibold">{{ form.parent_id ? 'Add Region' : 'Add New State' }}</h2>
      </template>
      <template #body>
        <div class="grid grid-cols-1 gap-4 min-w-80 pb-48">
          <div>
            <label for="new-location-name" class="block text-sm font-medium text-bhav-blue-300 mb-1">
              Name <span class="text-bhav-red cursor-help" title="This field is required">*</span>
            </label>
            <TextInput id="new-location-name" v-model="form.name" placeholder="Location name" />
          </div>
          <div>
            <label for="new-location-parent" class="block text-sm font-medium text-bhav-blue-300 mb-1">
              State
            </label>
            <SelectField id="new-location-parent" v-model="form.parent_id" :options="parentOptions" empty="None (top-level region)" />
          </div>
        </div>
      </template>
      <template #buttons>
        <FormButton @click="store" :disabled="!form.name" color="blue">Save</FormButton>
        <FormButton @click="cancelCreate" color="gray">Cancel</FormButton>
      </template>
    </ModalDialog>

    <!-- Delete modal -->
    <ModalDialog v-if="deleting" :no-padding="true" :open="!!deleting" @close="cancelDelete">
      <template #header>
        <h2 class="text-lg text-bhav-blue-600 font-semibold">Confirm Location Deletion</h2>
      </template>
      <template #body>
        <p class="text-bhav-blue-500"><strong>Attention!</strong> You are about to delete <em>{{ deleting?.name }}</em>.</p>
        <p v-if="!deleting?.parent_id" class="mt-2 text-sm font-medium text-bhav-red">
          All regions inside this location will also be deleted and will no longer appear in the system.
        </p>
        <p class="mt-2">Are you sure?</p>
      </template>
      <template #buttons>
        <FormButton type="button" @click="destroy" prefix-class="fak" icon="fa-trash" color="red" />
        <FormButton type="button" @click="cancelDelete" color="gray">Cancel</FormButton>
      </template>
    </ModalDialog>

    <!-- Add Community modal -->
    <ModalDialog v-if="attachingChild" :open="!!attachingChild" @close="cancelAttach" :no-padding="true">
      <template #header>
        <h2 class="text-lg text-bhav-blue-600 font-semibold">Add Community to <em class="font-normal">{{ attachingChild.name }}</em></h2>
      </template>
      <template #body>
        <div class="flex flex-col gap-3 min-w-96">
          <TextInput v-model="communitySearch" placeholder="Search communities..." />
          <div class="flex flex-col max-h-80 overflow-y-auto rounded-lg">                                                                
            <LoadingView v-if="loadingCommunities" :absolute="true" />
            <template v-else>
              <p v-if="!filteredCommunities.length" class="px-4 py-3 text-sm text-bhav-cool-gray-400 italic">No communities found.</p>
              <button
                v-for="community in filteredCommunities"
                :key="community.id"
                type="button"
                @click="attachCommunity(community)"
                class="flex items-center justify-between px-4 py-3 text-left hover:bg-bhav-cool-gray-50 border-b border-bhav-cool-gray-100 last:border-b-0 transition-colors">
                <span class="text-sm text-bhav-blue-600">{{ community.meta?.info?.name || community.slug }}</span>
                <span v-if="community.location_id" class="text-xs text-bhav-cool-gray-400 ml-2 flex-shrink-0">{{ locationLabel(community.location_id) }}</span>
              </button>
            </template>
          </div>
        </div>
      </template>
      <template #buttons>
        <FormButton @click="cancelAttach" color="gray">Close</FormButton>
      </template>
    </ModalDialog>

    <!-- Edit Location modal -->
    <ModalDialog v-if="editing" :open="!!editing" @close="cancelEdit" :no-padding="true">
      <template #header>
        <h2 class="text-lg text-bhav-blue-600 font-semibold">Edit Location</h2>
      </template>
      <template #body>
        <div class="grid grid-cols-1 gap-4 min-w-80 pb-48">
          <div>
            <label for="edit-location-name" class="block text-sm font-medium text-bhav-blue-300 mb-1">
              Name <span class="text-bhav-red cursor-help" title="This field is required">*</span>
            </label>
            <TextInput id="edit-location-name" v-model="editing.name" placeholder="Location name" />
          </div>
          <div>
            <label for="edit-location-parent" class="block text-sm font-medium text-bhav-blue-300 mb-1">
              State
            </label>
            <SelectField id="edit-location-parent" v-model="editing.parent_id" :options="editParentOptions" empty="None (top-level region)" />
          </div>
        </div>
      </template>
      <template #buttons>
        <FormButton @click="update" :disabled="!editing.name" color="blue">Save Changes</FormButton>
        <FormButton @click="cancelEdit" color="gray">Cancel</FormButton>
      </template>
    </ModalDialog>

    <!-- Detach confirmation modal -->
    <ModalDialog v-if="detaching" :open="!!detaching" @close="cancelDetach" :no-padding="true">
      <template #header>
        <h2 class="text-lg text-bhav-blue-600 font-semibold">Remove from Location</h2>
      </template>
      <template #body>
        <p class="text-bhav-blue-500">
          Remove <em>{{ detaching?.community?.meta?.info?.name || detaching?.community?.slug }}</em> from this location?
        </p>
        <p class="mt-2 text-sm text-bhav-cool-gray-500">The community will remain, but will no longer be assigned to any location.</p>
      </template>
      <template #buttons>
        <FormButton type="button" @click="detachCommunity" color="red">Remove</FormButton>
        <FormButton type="button" @click="cancelDetach" color="gray">Cancel</FormButton>
      </template>
    </ModalDialog>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, SelectField, TextInput } from '@/components/form';
import SearchInput from '@/components/form/SearchInput.vue';
import ModalDialog from '@/components/ModalDialog.vue';

const emptyForm = { name: '', parent_id: null };
const defaultQuery = { search: '', parent_id: null };

export default {
  data() {
    return {
      locations: [],
      isLoading: true,
      openParents: new Set(),
      openChildren: new Set(),
      creating: false,
      deleting: null,
      form: { ...emptyForm },
      attachingChild: null,
      communities: [],
      communitySearch: '',
      loadingCommunities: false,
      detaching: null,
      editing: null,
      query: { ...defaultQuery }
    };
  },
  computed: {
    parentOptions() {
      return this.locations.map(loc => ({ value: loc.id, label: loc.name }));
    },
    filteredLocations() {
      let results = this.locations;
      if (this.query.parent_id) {
        results = results.filter(p => p.id === this.query.parent_id);
      }
      const q = this.query.search.trim().toLowerCase();
      if (q) {
        results = results.reduce((acc, parent) => {
          if (parent.name.toLowerCase().includes(q)) {
            acc.push(parent);
            return acc;
          }
          const matchingChildren = parent.locations?.filter(c => c.name.toLowerCase().includes(q)) ?? [];
          if (matchingChildren.length) acc.push({ ...parent, locations: matchingChildren });
          return acc;
        }, []);
      }
      return results;
    },
    hasFilters() {
      return JSON.stringify(this.query) !== JSON.stringify(defaultQuery);
    },
    editParentOptions() {
      return this.locations
        .filter(loc => loc.id !== this.editing?.id)
        .map(loc => ({ value: loc.id, label: loc.name }));
    },
    filteredCommunities() {
      const q = this.communitySearch.trim().toLowerCase();
      if (!q) return this.communities;
      return this.communities.filter(c => {
        const name = (c.meta?.info?.name || c.slug || '').toLowerCase();
        return name.includes(q);
      });
    }
  },
  methods: {
    index() {
      this.isLoading = true;
      axios.get('locations')
        .then(res => {
          this.locations = res.data;
        })
        .catch(e => { throw e; })
        .finally(() => { this.isLoading = false; });
    },
    toggleParent(id) {
      const next = new Set(this.openParents);
      next.has(id) ? next.delete(id) : next.add(id);
      this.openParents = next;
    },
    toggleChild(id) {
      const next = new Set(this.openChildren);
      next.has(id) ? next.delete(id) : next.add(id);
      this.openChildren = next;
    },
    openCreate(parent = null) {
      this.form = { ...emptyForm, parent_id: parent?.id ?? null };
      this.creating = true;
    },
    cancelCreate() {
      this.creating = false;
      this.form = { ...emptyForm };
    },
    store() {
      if (!this.form.name) return;
      axios.post('locations', this.form)
        .then(() => {
          this.cancelCreate();
          this.index();
        })
        .catch(e => { throw e; });
    },
    openDelete(item) {
      this.deleting = item;
    },
    cancelDelete() {
      this.deleting = null;
    },
    destroy() {
      axios.delete(`locations/${this.deleting.id}`)
        .then(() => {
          this.cancelDelete();
          this.index();
        })
        .catch(e => { throw e; });
    },
    openAttach(child) {
      this.attachingChild = child;
      this.communitySearch = '';
      this.loadingCommunities = true;
      axios.get('divisions/communities')
        .then(res => {
          const assigned = new Set(this.attachingChild.divisions?.map(d => d.id) ?? []);
          this.communities = res.data.filter(c => c.status !== 'inactive' && !assigned.has(c.id));
        })
        .catch(e => { throw e; })
        .finally(() => { this.loadingCommunities = false; });
    },
    cancelAttach() {
      this.attachingChild = null;
      this.communitySearch = '';
    },
    attachCommunity(community) {
      axios.patch(`divisions/${community.id}`, { location_id: this.attachingChild.id })
        .then(() => {
          this.cancelAttach();
          this.index();
        })
        .catch(e => { throw e; });
    },
    locationLabel(locationId) {
      for (const parent of this.locations) {
        const child = parent.locations?.find(l => l.id === locationId);
        if (child) return `${parent.name} › ${child.name}`;
      }
      return '';
    },
    reset() {
      this.query = { ...defaultQuery };
    },
    openEdit(location) {
      this.editing = { id: location.id, name: location.name, parent_id: location.parent_id ?? null };
    },
    cancelEdit() {
      this.editing = null;
    },
    update() {
      if (!this.editing.name) return;
      axios.patch(`locations/${this.editing.id}`, { name: this.editing.name, parent_id: this.editing.parent_id })
        .then(() => {
          this.cancelEdit();
          this.index();
        })
        .catch(e => { throw e; });
    },
    openDetach(division, childId) {
      this.detaching = { community: division, childId };
    },
    cancelDetach() {
      this.detaching = null;
    },
    detachCommunity() {
      axios.patch(`divisions/${this.detaching.community.id}`, { location_id: null })
        .then(() => {
          this.cancelDetach();
          this.index();
        })
        .catch(e => { throw e; });
    }
  },
  created() {
    this.index();
  },
  components: { LoadingView, FormButton, SelectField, TextInput, ModalDialog, SearchInput }
};
</script>
